/**
 * Browser Build - Auto-initializing standalone bundle for CDN usage
 *
 * Usage:
 * <script src="https://cdn.../behavior-detector.js"></script>
 * <script>
 * window.bdSettings = {
 *   strategies: {
 *     mouse: { weight: 0.3 },
 *     scroll: { weight: 0.15 },
 *     click: { weight: 0.3 },
 *     keyboard: { weight: 0.1 },
 *     environment: { weight: 0.08 },
 *     resize: { weight: 0.02 }
 *   },
 *   autoStart: true,
 *   checkMs: 2000,
 *   pauseOnHidden: true,  // Auto-pause when tab hidden (default: true)
 *   onScore: (result) => {
 *     console.log('Human likelihood:', result.score);
 *     if (result.score < 0.3) {
 *       // Likely bot
 *     }
 *   },
 *   ifBot: (result) => {
 *     // Called when score < threshold
 *     console.warn('Bot detected:', result);
 *   },
 *   botThreshold: 0.3
 * }
 * </script>
 */
import { BehaviorDetector } from './behavior-detector.js';
import { MouseStrategy, ScrollStrategy, ClickStrategy, TapStrategy, KeyboardStrategy, EnvironmentStrategy, ResizeStrategy, } from './strategies/index.js';
/**
 * Detect if the current device is mobile
 */
function isMobileDevice() {
    const hasTouchScreen = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
    const mobileUA = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const smallScreen = window.innerWidth < 768 && window.innerHeight < 1024;
    return (hasTouchScreen && smallScreen) || mobileUA;
}
class BehaviorDetectionAPI {
    constructor() {
        this.detector = null;
        this.checkInterval = null;
        this.settings = null;
    }
    /**
     * Initialize the detector with settings
     */
    init(settings) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
        if (this.detector) {
            this.stop();
        }
        this.settings = settings;
        this.detector = new BehaviorDetector({
            pauseOnHidden: settings.pauseOnHidden !== undefined ? settings.pauseOnHidden : true
        });
        // Detect if mobile to adjust strategy selection and weights
        const isMobile = isMobileDevice();
        const strategies = settings.strategies || {};
        // Mobile-adjusted default weights
        const defaultWeights = isMobile ? {
            mouse: 0, // Disabled on mobile
            scroll: 0.35, // Increased (more reliable on mobile)
            click: 0, // Disabled on mobile (use tap instead)
            tap: 0.35, // Mobile tap detection
            keyboard: 0.15,
            environment: 0.10,
            resize: 0.05, // Orientation changes
        } : {
            mouse: 0.30,
            scroll: 0.15,
            click: 0.30,
            tap: 0, // Disabled on desktop
            keyboard: 0.10,
            environment: 0.08,
            resize: 0.02,
        };
        // Mouse - Desktop only
        if (!isMobile && ((_a = strategies.mouse) === null || _a === void 0 ? void 0 : _a.enabled) !== false) {
            const strategy = new MouseStrategy();
            const weight = (_c = (_b = strategies.mouse) === null || _b === void 0 ? void 0 : _b.weight) !== null && _c !== void 0 ? _c : defaultWeights.mouse;
            this.detector.addStrategy(strategy, weight);
        }
        // Scroll - Universal
        if (((_d = strategies.scroll) === null || _d === void 0 ? void 0 : _d.enabled) !== false) {
            const strategy = new ScrollStrategy();
            const weight = (_f = (_e = strategies.scroll) === null || _e === void 0 ? void 0 : _e.weight) !== null && _f !== void 0 ? _f : defaultWeights.scroll;
            this.detector.addStrategy(strategy, weight);
        }
        // Click - Desktop only
        if (!isMobile && ((_g = strategies.click) === null || _g === void 0 ? void 0 : _g.enabled) !== false) {
            const strategy = new ClickStrategy();
            const weight = (_j = (_h = strategies.click) === null || _h === void 0 ? void 0 : _h.weight) !== null && _j !== void 0 ? _j : defaultWeights.click;
            this.detector.addStrategy(strategy, weight);
        }
        // Tap - Mobile only
        if (isMobile && ((_k = strategies.tap) === null || _k === void 0 ? void 0 : _k.enabled) !== false) {
            const strategy = new TapStrategy();
            const weight = (_m = (_l = strategies.tap) === null || _l === void 0 ? void 0 : _l.weight) !== null && _m !== void 0 ? _m : defaultWeights.tap;
            this.detector.addStrategy(strategy, weight);
        }
        // Keyboard - Universal
        if (((_o = strategies.keyboard) === null || _o === void 0 ? void 0 : _o.enabled) !== false) {
            const strategy = new KeyboardStrategy();
            const weight = (_q = (_p = strategies.keyboard) === null || _p === void 0 ? void 0 : _p.weight) !== null && _q !== void 0 ? _q : defaultWeights.keyboard;
            this.detector.addStrategy(strategy, weight);
        }
        // Environment - Universal
        if (((_r = strategies.environment) === null || _r === void 0 ? void 0 : _r.enabled) !== false) {
            const strategy = new EnvironmentStrategy();
            const weight = (_t = (_s = strategies.environment) === null || _s === void 0 ? void 0 : _s.weight) !== null && _t !== void 0 ? _t : defaultWeights.environment;
            this.detector.addStrategy(strategy, weight);
        }
        // Resize - Universal
        if (((_u = strategies.resize) === null || _u === void 0 ? void 0 : _u.enabled) !== false) {
            const strategy = new ResizeStrategy();
            const weight = (_w = (_v = strategies.resize) === null || _v === void 0 ? void 0 : _v.weight) !== null && _w !== void 0 ? _w : defaultWeights.resize;
            this.detector.addStrategy(strategy, weight);
        }
        // Start if autoStart is enabled
        if (settings.autoStart !== false) {
            this.start();
        }
        // Set up periodic checking if checkMs is specified
        if (settings.checkMs && settings.checkMs > 0) {
            this.startPeriodicCheck(settings.checkMs);
        }
        // Call onReady callback
        if (settings.onReady) {
            settings.onReady(this);
        }
    }
    /**
     * Start detection
     */
    start() {
        if (!this.detector) {
            return;
        }
        this.detector.start();
    }
    /**
     * Stop detection
     */
    stop() {
        if (this.detector) {
            this.detector.stop();
        }
        if (this.checkInterval !== null) {
            clearInterval(this.checkInterval);
            this.checkInterval = null;
        }
    }
    /**
     * Get current score
     */
    async score() {
        if (!this.detector) {
            return null;
        }
        return this.detector.score({ breakdown: true });
    }
    /**
     * Start periodic checking
     */
    startPeriodicCheck(intervalMs) {
        if (this.checkInterval !== null) {
            clearInterval(this.checkInterval);
        }
        this.checkInterval = window.setInterval(async () => {
            var _a, _b;
            const result = await this.score();
            if (!result || !this.settings)
                return;
            // Call onScore callback
            if (this.settings.onScore) {
                this.settings.onScore(result);
            }
            // Check thresholds
            const botThreshold = (_a = this.settings.botThreshold) !== null && _a !== void 0 ? _a : 0.3;
            const humanThreshold = (_b = this.settings.humanThreshold) !== null && _b !== void 0 ? _b : 0.7;
            if (result.score < botThreshold && this.settings.ifBot) {
                this.settings.ifBot(result);
            }
            else if (result.score >= humanThreshold && this.settings.ifHuman) {
                this.settings.ifHuman(result);
            }
        }, intervalMs);
    }
    /**
     * Get the underlying detector instance for advanced usage
     */
    getDetector() {
        return this.detector;
    }
}
// Create global API instance
const api = new BehaviorDetectionAPI();
// Auto-initialize if settings are already present
if (typeof window !== 'undefined') {
    // Expose API globally
    window.BehaviorDetector = api;
    // Check for settings on load
    const checkAndInit = () => {
        const settings = window.bdSettings;
        if (settings) {
            api.init(settings);
        }
    };
    // Try immediate init
    checkAndInit();
    // Also try after DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', checkAndInit);
    }
}
// No exports needed for browser IIFE build - window.BehaviorDetector is sufficient
